import { useEffect, useState } from 'react';
import { MIN_WIDTH } from './constants';

type DragDirection = 'left' | 'right' | 'bottom';

type Params = {
	/** Callback triggered when the image is resized */
	onChangeWidth: (width: number) => void;
	/** The initial width of the element. */
	initialWidth: number;
	/** The natural width of the element. */
	naturalWidth: number;
	/** A reference to the container of the element being resized. */
	containerRef: React.RefObject<HTMLDivElement>;
	/** A reference to the element being resized. */
	resizeRef: React.RefObject<HTMLDivElement>;
	/** Whether the element is resizable. */
	isResizable: boolean;
	/** Whether the image is being dragged. */
	shouldExpandBilaterally: boolean;
};

export function useDragResize(props: Params) {
	const [width, setWidth] = useState(props.initialWidth);
	const [maxWidth, setMaxWidth] = useState(800);
	const [offset, setOffset] = useState(0);
	const [widthAtDragStart, setWidthAtDragStart] = useState(props.initialWidth);
	const [dragging, setDragging] = useState<DragDirection>();

	const resetImage = (newImageWidth: number) => {
		setWidth(newImageWidth);
	};

	const constrainWidth = (w: number, max: number) => {
		const minWidth = Math.min(props.naturalWidth, MIN_WIDTH);
		return Math.round(Math.min(max, Math.max(w, minWidth)));
	};

	const handlePointerMove = (event: PointerEvent) => {
		event.preventDefault();

		let diffX;
		if (dragging === 'left') {
			diffX = offset - event.pageX;
		} else if (dragging === 'right') {
			diffX = event.pageX - offset;
		}

		if (diffX && widthAtDragStart) {
			const newWidth =
				widthAtDragStart + diffX * (props.shouldExpandBilaterally ? 2 : 1);
			const constrainedWidth = constrainWidth(newWidth, maxWidth);

			// Only use naturalWidth if the user is trying to expand beyond the natural width
			const finalWidth =
				newWidth >= props.naturalWidth ? props.naturalWidth : constrainedWidth;

			// optimistically update the image's container with the new width
			if (props.resizeRef.current) {
				props.resizeRef.current.style.width = `${finalWidth}px`;
			}

			setWidth(finalWidth);
		}
	};

	const handlePointerUp = (event: PointerEvent) => {
		event.preventDefault();
		event.stopPropagation();

		setOffset(0);
		setDragging(undefined);
		props.onChangeWidth(width);

		document.removeEventListener('pointerup', handlePointerUp);
		document.removeEventListener('pointermove', handlePointerMove);
	};

	const handleKeyDown = (event: KeyboardEvent) => {
		if (event.key === 'Escape') {
			event.preventDefault();
			event.stopPropagation();

			setWidth(widthAtDragStart);
			setDragging(undefined);

			// rollback the container's width
			if (props.resizeRef.current) {
				props.resizeRef.current.style.width = `${widthAtDragStart}px`;
			}
		}
	};

	const handlePointerDown =
		(dragDirection: DragDirection) =>
		(event: React.PointerEvent<HTMLDivElement>) => {
			event.preventDefault();
			event.stopPropagation();

			// get the width of the editor
			const max =
				props.containerRef.current?.closest('.ProseMirror')?.clientWidth ??
				Infinity;
			setMaxWidth(max);
			setWidthAtDragStart(constrainWidth(width, max));
			setOffset(
				dragDirection === 'left' || dragDirection === 'right'
					? event.pageX
					: event.pageY
			);
			setDragging(dragDirection);
		};

	useEffect(() => {
		if (!props.isResizable) {
			return () => {};
		}

		if (dragging) {
			document.body.style.cursor =
				dragging === 'left' || dragging === 'right' ? 'ew-resize' : 'ns-resize';
			document.addEventListener('keydown', handleKeyDown);
			document.addEventListener('pointermove', handlePointerMove);
			document.addEventListener('pointerup', handlePointerUp);
		}

		return () => {
			document.body.style.cursor = 'initial';
			document.removeEventListener('keydown', handleKeyDown);
			document.removeEventListener('pointermove', handlePointerMove);
			document.removeEventListener('pointerup', handlePointerUp);
		};
	}, [dragging, handlePointerMove, handlePointerUp, props.isResizable]);

	return {
		handlePointerDown,
		dragging: !!dragging,
		resetImage,
		maxWidth,
	};
}
